import { createOrder, setBasket } from '../../features/OrdersSlice'
import React, { useState } from 'react'
import { Button, InputNumber, Tooltip } from 'antd'
import { ShoppingCartOutlined, LoadingOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getCatalogueAddButtonState } from 'src/utils/growthEngineCatalogueQuickAdd'

const withAddReason = (node, tooltip) =>
  tooltip ? (
    <Tooltip title={tooltip}>
      <span style={{ display: 'inline-flex' }}>{node}</span>
    </Tooltip>
  ) : (
    node
  )

const BasketFunctionality = ({ venueProducts, venueId, order }) => {
  const dispatch = useDispatch()
  const basket = useSelector(state => state.orders.basket)
  const [addProductId, setAddProductId] = useState('')
  const updateLoading = useSelector(state => state.orders.isUpdateLoading)
  const basketProductsIds = basket.map(product => product.product._id)
  const venueProductsIds = venueProducts
    .filter(prod => prod.product)
    .map(vProduct => vProduct.product._id)

  const addToBasket = order => {
    setAddProductId(order._id)
    const venueProduct = venueProducts.find(p => p.product._id === order._id)
    if (!venueProduct) return

    // Check if an order for this product already exists in the basket
    // This prevents duplicate basket items for the same product
    const existingOrder = basket.find(
      basketItem =>
        basketItem.product._id === order._id &&
        basketItem.supplier === venueProduct.supplier,
    )

    if (existingOrder) {
      // Update existing order quantity instead of creating duplicate
      dispatch(
        createOrder({
          ...existingOrder,
          _id: existingOrder._id, // Preserve order _id for update
          quantity:
            (existingOrder.quantity || 0) + (venueProduct.defaultQuantity || 1),
          method: 'update',
        }),
      )
    } else {
      // Create new order - keep venueProductId for backend reference
      const { _id: venueProductId, ...res } = venueProduct
      dispatch(
        createOrder({
          ...res,
          venueProductId, // Pass venueProduct._id separately for backend
          isSpecialOrder: res?.isSpecialProduct ? true : false,
          specialProductPrice:
            res?.isSpecialProduct && !isNaN(res?.specialPrice)
              ? res?.specialPrice
              : 0,
          quantity: res.defaultQuantity || 1,
          OMSSpecialPrice:
            res.OMSSpecialPrice !== 'NaN' ? res.OMSSpecialPrice : 0,
          method: 'create',
        }),
      )
    }
  }
  const pageName = useLocation().pathname.split('/').reverse()[0]

  const updateOrder = order => {
    dispatch(
      createOrder({
        ...order,
        quantity: parseFloat(order.quantity),
        OMSSpecialPrice: order.OMSSpecialPrice ? order.OMSSpecialPrice : 0,
        method: 'update',
      }),
    )
  }
  const onChangeQuantity = (value, product) => {
    dispatch(
      setBasket(
        basket.map(prod => {
          if (prod.product._id === product._id) {
            const newObj = {
              ...prod,
              quantity: value,
            }
            updateOrder(newObj)
            return newObj
          } else return prod
        }),
      ),
    )
  }

  const handleSetQuantity = product => {
    return basket.find(order => {
      return order.product._id === product._id
    }).quantity
  }

  const addState = getCatalogueAddButtonState({
    target: 'basket',
    onShoppingList: venueProductsIds.includes(order._id),
    inBasket: basketProductsIds.includes(order._id),
    adding: updateLoading && addProductId === order._id,
  })

  if (addState.showQuantity) {
    return withAddReason(
      <InputNumber
        style={{ width: '70px' }}
        onChange={value => onChangeQuantity(value, order)}
        value={handleSetQuantity(order)}
        aria-label={addState.label}
      />,
      addState.tooltip,
    )
  }

  const showLabel = pageName === 'catalogue' || Boolean(addState.tooltip)

  return withAddReason(
    <Button
      disabled={addState.disabled}
      onClick={() => addToBasket(order, venueId)}
      type={'primary'}
      style={{ display: 'flex', alignItems: 'center' }}
      aria-label={addState.tooltip || addState.label}
    >
      {updateLoading && addProductId === order._id ? (
        <LoadingOutlined />
      ) : (
        <ShoppingCartOutlined />
      )}{' '}
      {showLabel && <span>{addState.label}</span>}
    </Button>,
    addState.tooltip,
  )
}

export default BasketFunctionality
